import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Modal,
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  InputSize,
  Select,
  IconButton,
  Show,
  Badge,
  BadgeVariant,
  BadgeSize
} from "shared/components";
import { Checkbox } from "shared/components/forms/Checkbox";
import { Table } from "shared/app/Table";
import { Pagination } from "shared/components/Pagination";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { getErrorMessage } from "utils/error";
import { keepPreviousData } from "@tanstack/react-query";
import addressApi, {
  QUERY_GET_APPFOLIO_PROPERTIES,
  MUTATION_ADD_PROPERTY_BY_ID,
  MUTATION_EXCLUDE_PROPERTY,
  MUTATION_INCLUDE_PROPERTY
} from "api/landlord/address";
import { AppfolioProperty } from "./types";
import { SearchSM } from "untitledui-js-base";
import { Cancel01Icon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";

const PER_PAGE = 10;
const SELECT_OPTIONS = [
  { label: "Property Name", value: "propertyName" },
  { label: "Address", value: "address1" },
  { label: "City", value: "city" },
  { label: "State", value: "state" },
  { label: "ZIP", value: "zip" },
  { label: "Owner", value: "owners" }
];

const SORT_OPTIONS = [
  { label: "Ascending", value: "asc" },
  { label: "Descending", value: "desc" }
];

const FILTER_OPTIONS = [
  { label: "All Properties", value: "all" },
  { label: "Excluded", value: "excluded" },
  { label: "In Boom", value: "in_boom" }
];

const getErrorSubtext = ({
  hasSearchTerm,
  hasError
}: {
  hasSearchTerm: boolean;
  hasError: boolean;
}) => {
  if (hasSearchTerm)
    return "We couldn't find any properties matching your search";
  if (hasError) return "Failed to load Appfolio properties. Please try again.";
  return "No Appfolio properties are available to add";
};

export const AddAppfolioPropertyModal = ({
  opened,
  onClose,
  onSuccess
}: {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("propertyName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      QUERY_GET_APPFOLIO_PROPERTIES,
      page,
      debouncedSearchTerm,
      sortBy,
      sortOrder,
      statusFilter
    ],
    queryFn: () => {
      return addressApi.getAppfolioProperties({
        page,
        limit: PER_PAGE,
        search: debouncedSearchTerm,
        sortBy,
        sortOrder,
        statusFilter
      });
    },
    enabled: opened,
    placeholderData: keepPreviousData
  });

  const { mutate: addProperty, isPending: isAdding } = useMutation({
    mutationFn: (propertyId: string) => addressApi.addPropertyById(propertyId),
    onSuccess: () => {
      toast.success("Property added successfully");
      // Refresh the data to show updated status
      queryClient.invalidateQueries({
        queryKey: [QUERY_GET_APPFOLIO_PROPERTIES]
      });
      onSuccess();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    mutationKey: [MUTATION_ADD_PROPERTY_BY_ID]
  });

  const { mutate: excludeProperty, isPending: isExcluding } = useMutation({
    mutationFn: (propertyId: string) => addressApi.excludeProperty(propertyId),
    onSuccess: () => {
      toast.success("Property excluded from Boom");
      // Refresh the data to show updated status
      queryClient.invalidateQueries({
        queryKey: [QUERY_GET_APPFOLIO_PROPERTIES]
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    mutationKey: [MUTATION_EXCLUDE_PROPERTY]
  });

  const { mutate: includeProperty, isPending: isIncluding } = useMutation({
    mutationFn: (propertyId: string) => addressApi.includeProperty(propertyId),
    onSuccess: () => {
      toast.success("Property exclusion cancelled");
      // Refresh the data to show updated status
      queryClient.invalidateQueries({
        queryKey: [QUERY_GET_APPFOLIO_PROPERTIES]
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    mutationKey: [MUTATION_INCLUDE_PROPERTY]
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "propertyName",
        header: "Property Name",
        size: 200,
        cell: ({ row }) => {
          const property = row.original as AppfolioProperty;
          const status = property.status;

          return (
            <div className="max-w-[200px] w-full">
              <div className="flex items-center gap-1">
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-medium text-content-1000 truncate"
                    title={property.propertyName}
                  >
                    {property.propertyName}
                  </p>
                </div>
                {status && (
                  <div className="flex gap-1 flex-shrink-0">
                    {status.exists_in_db && (
                      <Badge
                        variant={BadgeVariant.SUCCESS}
                        size={BadgeSize.SM}
                        tooltip="Added to Boom"
                      >
                        •
                      </Badge>
                    )}
                    {status.is_excluded && (
                      <Badge
                        variant={BadgeVariant.ERROR}
                        size={BadgeSize.SM}
                        tooltip="Excluded from Boom"
                      >
                        •
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-content-400 truncate">
                ID: {property.propertyId}
              </p>
            </div>
          );
        }
      },
      {
        accessorKey: "address1",
        header: "Address",
        size: 250,
        cell: ({ row }) => {
          const property = row.original as AppfolioProperty;
          return (
            <div className="max-w-[250px]">
              <p
                className="text-sm text-content-1000 truncate"
                title={property.address1}
              >
                {property.address1}
              </p>
              <p className="text-xs text-content-400 truncate">
                {property.city}, {property.state} {property.zip}
              </p>
            </div>
          );
        }
      },
      {
        accessorKey: "owners",
        header: "Owner(s)",
        size: 200,
        cell: ({ getValue }) => {
          const ownerValue = getValue() || "--";
          return (
            <p
              className="text-sm text-content-1000 truncate max-w-[200px]"
              title={ownerValue}
            >
              {ownerValue}
            </p>
          );
        }
      },
      {
        id: "add_action",
        header: "Action",
        size: 80,
        cell: (p) => {
          const property = p.row.original as AppfolioProperty;
          const status = property.status;
          const isExcluded =
            status?.is_excluded || property.excludedFromListing;
          const alreadyExists = status?.exists_in_db || false;

          return (
            <Button
              variant={
                alreadyExists ? ButtonVariant.BASE : ButtonVariant.PRIMARY
              }
              size={ButtonSize.XS}
              HugeIcon={PlusSignIcon}
              loading={isAdding}
              disabled={alreadyExists || isExcluded}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                addProperty(property.propertyId);
              }}
              title={(() => {
                if (isExcluded) return "Property ignored";
                if (alreadyExists) return "Already added";
                return "Add property";
              })()}
            >
              {alreadyExists ? "Added" : "Add"}
            </Button>
          );
        }
      },
      {
        id: "ignore_action",
        header: "Ignore",
        size: 60,
        cell: (p) => {
          const property = p.row.original as AppfolioProperty;
          const status = property.status;
          const isExcluded =
            status?.is_excluded || property.excludedFromListing;

          return (
            <div className="flex justify-center">
              <Checkbox
                key={`ignore-${property.propertyId}-${!!isExcluded}`}
                checked={isExcluded}
                disabled={isExcluding || isIncluding}
                onChange={(e) => {
                  e.stopPropagation();
                  if (isExcluded) includeProperty(property.propertyId);
                  else excludeProperty(property.propertyId);
                }}
              />
            </div>
          );
        }
      }
    ],
    [isAdding, isExcluding, isIncluding]
  );

  const rows = useMemo(() => {
    return data?.data || [];
  }, [data]);

  const table = useReactTable<AppfolioProperty>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.propertyId
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, sortBy, sortOrder, statusFilter]);

  return (
    <Modal
      onClose={() => {
        onClose();
      }}
      opened={opened}
      panelClassName="max-w-[1000px] w-full p-6"
    >
      <header className="flex justify-between mb-6 items-center">
        <h1 className="text-xl font-bold">
          Browse and add properties from Appfolio
        </h1>

        <IconButton HugeIcon={Cancel01Icon} borderLess onClick={onClose} />
      </header>
      {/* Search, Filter and Sort Controls */}
      <div className="mb-4 flex flex-col gap-4">
        <div className="flex-1 max-w-md">
          <Input
            id="search"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size={InputSize.SM}
          />
        </div>

        {/* Filter Section */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-content-600">Filter</label>
          <div className="flex gap-2">
            <Select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size={InputSize.SM}
              className="min-w-[140px]"
              options={FILTER_OPTIONS}
            />
          </div>
        </div>

        {/* Sort Section */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-content-600">Sort</label>
          <div className="flex gap-2">
            <Select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              size={InputSize.SM}
              className="min-w-[140px]"
              options={SELECT_OPTIONS}
            />
            <Select
              id="sortOrder"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
              size={InputSize.SM}
              className="min-w-[100px]"
              options={SORT_OPTIONS}
            />
          </div>
        </div>
      </div>
      <div className="max-h-[670px] h-full overflow-y-auto">
        <Table
          maw={1000}
          emptyRowsNumber={PER_PAGE}
          table={table}
          isLoading={isLoading}
          emptyStateTitle={
            debouncedSearchTerm ? "No results found" : "No properties found"
          }
          emptyStateText={getErrorSubtext({
            hasError: isError,
            hasSearchTerm: Boolean(debouncedSearchTerm)
          })}
          EmptyStateIcon={SearchSM}
          rowHeight="56px"
        />
      </div>
      {/* Pagination */}
      <Show if={data?.pagination.pages > 1}>
        <div className="mt-4">
          <Pagination
            current={page}
            total={data?.pagination?.pages}
            onChange={setPage}
          />
        </div>
      </Show>
    </Modal>
  );
};
